import { Typography } from "@mui/material";
import { useState, useEffect, useContext } from "react";
import { useSubGroups } from "../../../shared/hooks/useItemsData";
import SubGroupItem from "./SubGroupItem";
import {
    SortableContainer,
    SortableElement,
    arrayMove,
} from "react-sortable-hoc";
import { useSaveUserSettings } from "../../../shared/hooks/useFrappeDoctype";
import { AppContext } from "../../../AppContext";
import Scrollbar from "../../../components/Scrollbar/Scrollbar";

// SortableItem component for subgroups
const SortableItem = SortableElement(
    ({ subGroup, active, onClick, isDraggable }) => (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                marginLeft: "5px",
                marginRight: "5px",
                cursor: isDraggable ? "grab" : "pointer",
            }}
        >
            <SubGroupItem
                active={active}
                subGroup={subGroup}
                onClick={onClick}
            />
            <span
                style={{
                    fontSize: "16px",
                    fontWeight: "700",
                    whiteSpace: "nowrap",
                }}
            >
                {subGroup.name}
            </span>
        </div>
    )
);

// SortableList component for subgroups
const SortableList = SortableContainer(
    ({ subGroups, activeSubGroup, onClick, isDraggable }) => (
        <Scrollbar
            sx={{
                height: "100%",
                "& .simplebar-content": {
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    height: "100%",
                    paddingBottom: "5px",
                },
            }}
        >
            <div className="ItemsList">
                {subGroups.map((subGroup, index) => (
                    <SortableItem
                        key={`subgroup-${subGroup.id}`}
                        index={index}
                        subGroup={subGroup}
                        active={activeSubGroup === subGroup.id}
                        onClick={onClick}
                        isDraggable={isDraggable}
                    />
                ))}
            </div>
        </Scrollbar>
    )
);

export default function SubGroupList({
    parentItem,
    onClick,
    isDragDropMode = false,
}) {
    const { data } = useSubGroups(parentItem ? parentItem.id : null);
    const { userMenuSettings } = useContext(AppContext);
    const saveUserSettings = useSaveUserSettings();

    const [activeSubGroup, setActiveSubGroup] = useState(null);
    const [sortedSubGroups, setSortedSubGroups] = useState([]);

    const handleClick = (subGroup) => {
        onClick(subGroup);
        setActiveSubGroup(subGroup.id);
    };

    useEffect(() => {
        if (data && data.length > 0) {
            let subGroups = [...data];

            // Sort subgroups based on saved order
            if (
                userMenuSettings &&
                userMenuSettings.sub_group_index &&
                userMenuSettings.sub_group_index.length > 0
            ) {
                const subGroupIndexMap =
                    userMenuSettings.sub_group_index.reduce((acc, item) => {
                        acc[item.subgroup_id] = item.index;
                        return acc;
                    }, {});

                subGroups = subGroups.sort((a, b) => {
                    const indexA = subGroupIndexMap[a.id] || 0;
                    const indexB = subGroupIndexMap[b.id] || 0;
                    return indexA - indexB;
                });
            }

            setSortedSubGroups(subGroups);
        } else {
            setSortedSubGroups([]);
        }
    }, [data, userMenuSettings]);

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const updatedSubGroups = arrayMove(sortedSubGroups, oldIndex, newIndex);
        setSortedSubGroups(updatedSubGroups);
        handleSaveSubGroupOrder(updatedSubGroups);
    };

    const handleSaveSubGroupOrder = (reorderedSubGroups) => {
        if (userMenuSettings) {
            const _userSettings = { ...userMenuSettings };
            _userSettings.sub_group_index = reorderedSubGroups.map(
                (subGroup, index) => ({
                    subgroup_id: subGroup.id,
                    index,
                })
            );
            saveUserSettings.mutate(
                {
                    doctype: "menu_settings",
                    user_settings: _userSettings,
                },
                {
                    onSuccess: () => {
                        console.log("SubGroup order saved!");
                    },
                }
            );
        }
    };

    return (
        <div
            style={{
                height: "100%",
                width: "100%",
                display: "flex",
                alignItems: "center",
                felx: 1,
            }}
        >
            <div
                className={"Header__Title"}
                style={{
                    marginRight: "12px",
                    flexShrink: 0,
                    minWidth: "auto",
                    width: "auto",
                }}
            >
                <div onClick={handleClick}>
                    {parentItem ? parentItem.name : ""}
                </div>
                <div onClick={handleClick}>SubCategories</div>
            </div>
            <div
                style={{ flex: 1, height: "100%", minWidth: 0, width: "100%" }}
            >
                {isDragDropMode ? (
                    <SortableList
                        subGroups={sortedSubGroups}
                        onSortEnd={onSortEnd}
                        activeSubGroup={activeSubGroup}
                        onClick={handleClick}
                        axis="x"
                        isDraggable={isDragDropMode}
                    />
                ) : (
                    <Scrollbar
                        sx={{
                            height: "100%",
                            "& .simplebar-content": {
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                height: "100%",
                                paddingBottom: "5px",
                            },
                        }}
                    >
                        <div className="ItemsList">
                            {sortedSubGroups && sortedSubGroups.length > 0 ? (
                                sortedSubGroups.map((item) => (
                                    <div
                                        key={
                                            (parentItem?.name || "default") +
                                            "_" +
                                            item.name +
                                            "_SubGroupItem"
                                        }
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            marginLeft: "5px",
                                            marginRight: "5px",
                                        }}
                                    >
                                        <SubGroupItem
                                            active={activeSubGroup === item.id}
                                            subGroup={item}
                                            onClick={handleClick}
                                        />
                                        <span
                                            style={{
                                                fontSize: "16px",
                                                fontWeight: "700",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {item.name}
                                        </span>
                                    </div>
                                ))
                            ) : (
                                <Typography variant="body2">
                                    No subcategories found
                                </Typography>
                            )}
                        </div>
                    </Scrollbar>
                )}
            </div>
        </div>
    );
}
